'use client';

import { Button } from '@common/components';
import { useClipboard, useToast } from '@common/hooks';
import { SvgShare2 } from '@common/icon';
import { cn, isDev } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { UnauthQueryOptions } from '@/api';
import { DONATION_ROUTES, ENV, ROUTES } from '@/constants';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useKakaoShare } from '@/hooks/useKakaoShare';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';

import { INVITE_CONFIG } from '../../constants';

interface ShareButtonsProps {
  endDate: string | Date;
}

export const ShareButtons = ({ endDate }: ShareButtonsProps) => {
  const copy = useClipboard();
  const currentWebviewType = useDeviceInfoStore(
    useShallow((state) => state.webviewType),
  );
  const [isUserLoggedIn, userMemberCode] = useAuthStore(
    useShallow((state) => [state.isLogin, state.myInfo?.memberCode]),
  );
  const { data: name, isLoading: isNameLoading } = useQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/home-donation-event-history/member-name'
    ]({
      query: {
        memberCode: userMemberCode as string,
      },
    }),
    enabled: !!userMemberCode,
    select: ({ context }) => context?.name,
  });

  const { isWelloApp } = useCheckWelloApp();
  const isKakaoBank = currentWebviewType === WEBVIEW_TYPE.KB_PAY;
  const { isInitialized: isKakaoSdkInitialized } = useKakaoShare();
  const router = useCustomRouter();
  const currentPathname = usePathname();
  const { toast } = useToast();

  const inviteShareUrl = generateShareLinkUrl(userMemberCode);

  const {
    request: checkKakaoInstallRequest,
    response: kakaoInstallationResponse,
  } = useHandleApp({
    type: MESSAGE_KEYWORD.CHECK_KAKAO_INSTALLED,
  });

  useEffect(() => {
    checkKakaoInstallRequest();
  }, [checkKakaoInstallRequest]);

  const { request: requestNativeShare, isPending: isNativeSharePending } =
    useHandleApp({
      type: MESSAGE_KEYWORD.SHARE_CONTENT,
    });

  const navigateToMyBenefits = () => {
    const benefitsPageUrl = DONATION_ROUTES.GUIDE.withSearchParams({
      eventParams: {
        utm_source: 'wello',
        utm_medium: 'invite',
        utm_campaign: 'cta',
        utm_content: 'button',
      },
    });

    router.push(benefitsPageUrl);
  };

  const initiateKakaoShare = async () => {
    if (!isUserLoggedIn) {
      const loginPageUrl = ROUTES.LOGIN.withSearchParams({
        searchParams: {
          redirect: currentPathname || '',
        },
      });

      router.push(loginPageUrl);

      return;
    }

    if (isWelloApp) {
      await executeNativeKakaoShare();
    } else {
      executeWebKakaoShare();
    }
  };

  const executeNativeKakaoShare = async () => {
    if (isNativeSharePending) return;

    const isKakaoInstalled = kakaoInstallationResponse?.isSuccess;

    if (!inviteShareUrl) {
      toast({
        message: '공유 URL이 없습니다.',
        type: 'fail',
      });

      return;
    }

    if (isKakaoInstalled) {
      requestNativeShare({
        share_type: 'kakao',
        url: inviteShareUrl,
        thumbnail: INVITE_CONFIG.share.thumbnailImageUrl ?? '',
        subject: INVITE_CONFIG.share.title,
        description: INVITE_CONFIG.share.description,
      });
    } else {
      toast({
        message: '카카오톡이 설치되어 있지 않아요',
        type: 'fail',
      });
    }
  };

  const executeWebKakaoShare = () => {
    if (!inviteShareUrl) return;

    window.Kakao?.Share?.sendDefault?.({
      objectType: 'feed',
      content: {
        title: `${name}님이 134,000원을 선물했어요!🎁`,
        description: '친구 초대장으로 가입해야 134,000원을 받을 수 있어요.',
        imageUrl:
          'https://cdn.welfarehello.com/donation/event/invite/donation-invite-og-new-2.jpg',
        imageWidth: 1600,
        imageHeight: 800,
        link: {
          mobileWebUrl: inviteShareUrl,
          webUrl: inviteShareUrl,
        },
      },
      buttons: [
        {
          title: '지금 바로 선물받기',
          link: {
            mobileWebUrl: inviteShareUrl,
            webUrl: inviteShareUrl,
          },
        },
      ],
    });
  };

  const shouldShowKakaoShareButton = !isKakaoBank;

  const isDeadlinePassed = dayjs().isAfter(dayjs(endDate));

  return (
    <div className="flex items-center gap-8 px-20 py-12">
      <button
        type="button"
        onClick={navigateToMyBenefits}
        className={cn(
          'flex h-48 flex-1 items-center justify-center gap-8 rounded-xsmall bg-B-50 py-10 font-700 transition-all duration-200 typo-body3',
          !isDeadlinePassed && 'hover:bg-B-50/80 hover:shadow-lg',
          isDeadlinePassed && 'cursor-not-allowed opacity-50',
        )}
        disabled={isDeadlinePassed}
      >
        <GiftIcon />
        <span>나의 혜택받기</span>
      </button>

      {shouldShowKakaoShareButton && (
        <button
          type="button"
          onClick={initiateKakaoShare}
          className={cn(
            'flex h-48 flex-1 items-center justify-center gap-8 rounded-xsmall bg-[#FFDE32] py-10 font-700 transition-all duration-200 typo-body3',
            !isDeadlinePassed &&
              !!isKakaoSdkInitialized &&
              'hover:bg-Y-600 hover:shadow-lg',
            (isDeadlinePassed || !isKakaoSdkInitialized || isNameLoading) &&
              'cursor-not-allowed opacity-50',
          )}
          disabled={!isKakaoSdkInitialized || isDeadlinePassed || isNameLoading}
        >
          <KakaoIcon />
          <span>친구 초대하기</span>
        </button>
      )}
      <Button
        className="-mx-8"
        theme="sub-text"
        dimensions="H40"
        disabled={isDeadlinePassed}
        onClick={() => {
          if (!isUserLoggedIn) {
            const loginPageUrl = ROUTES.LOGIN.withSearchParams({
              searchParams: {
                redirect: currentPathname || '',
              },
            });

            router.push(loginPageUrl);

            return;
          }

          try {
            if (inviteShareUrl) {
              return copy({
                value: inviteShareUrl,
              });
            }
          } catch (e) {
            console.error(e);
          }
        }}
      >
        <SvgShare2 className="size-20 text-Gray-white" />
      </Button>
    </div>
  );
};

const generateShareLinkUrl = (memberCode?: string) => {
  const targetPathname = memberCode
    ? DONATION_ROUTES.DONATION_INVITE_RECEIVER.pathname({ memberCode })
    : DONATION_ROUTES.HOME.pathname;

  let baseDomain = ENV.NEXT_PUBLIC_DOMAIN;

  if (!isDev) {
    baseDomain = baseDomain.replace('https://', 'https://www.');
  }

  const fullShareUrl = new URL(targetPathname, baseDomain);

  return fullShareUrl.href;
};

const GiftIcon = () => (
  <svg width="17" height="16" viewBox="0 0 17 16" fill="none">
    <g clipPath="url(#clip0_16870_3212)">
      <path
        d="M14.9309 14.6914V7.17025H1.56865V14.6914C1.56865 14.9945 1.68905 15.2852 1.90338 15.4995C2.11771 15.7138 2.4084 15.8343 2.7115 15.8343H13.7881C14.0912 15.8343 14.3819 15.7138 14.5962 15.4995C14.8105 15.2852 14.9309 14.9945 14.9309 14.6914Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.05665 3.87539C6.94489 3.64729 6.81414 3.429 6.66579 3.22282C6.16979 2.53482 5.58579 2.14739 5.01779 2.14739C4.39493 2.14739 4.13779 2.32568 4.03379 2.44111C3.92179 2.56682 3.84865 2.76911 3.87379 3.06168C3.90007 3.35653 4.0235 3.65939 4.1915 3.87539H7.05665ZM2.29779 3.87539C2.23107 3.65961 2.18702 3.43745 2.16636 3.21253C2.1115 2.58168 2.2475 1.86168 2.76065 1.29253C3.28065 0.717677 4.0635 0.434248 5.01779 0.434248C6.38922 0.434248 7.42122 1.33939 8.05665 2.22168C8.12293 2.31425 8.18808 2.41025 8.24979 2.50739C8.3115 2.41025 8.3755 2.31425 8.44408 2.22168C9.07722 1.33711 10.1104 0.433105 11.4806 0.433105C12.4361 0.433105 13.2178 0.716534 13.7378 1.29368C14.2498 1.86168 14.3869 2.58168 14.3321 3.21368C14.3114 3.4386 14.2674 3.66075 14.2006 3.87653H14.8795C15.1826 3.87653 15.4733 3.99694 15.6876 4.21127C15.902 4.4256 16.0224 4.71629 16.0224 5.01939V6.02625C16.0224 6.32935 15.902 6.62004 15.6876 6.83437C15.4733 7.0487 15.1826 7.16911 14.8795 7.16911H8.96408V15.8342H7.5355V7.16911H1.61893C1.31583 7.16911 1.02514 7.0487 0.810809 6.83437C0.596482 6.62004 0.476074 6.32935 0.476074 6.02625V5.01825C0.476074 4.71514 0.596482 4.42445 0.810809 4.21013C1.02514 3.9958 1.31583 3.87539 1.61893 3.87539H2.29779ZM12.3081 3.87539C12.487 3.63875 12.5966 3.35702 12.6246 3.06168C12.6509 2.76911 12.5789 2.56568 12.4658 2.44111C12.3618 2.32568 12.1046 2.14739 11.4818 2.14739C10.9138 2.14739 10.3298 2.53596 9.83379 3.22168C9.67722 3.43882 9.54807 3.66282 9.44293 3.87539H12.3081Z"
        fill="#33A6FF"
      />
    </g>
    <defs>
      <clipPath id="clip0_16870_3212">
        <rect width="16" height="16" fill="white" transform="translate(0.25)" />
      </clipPath>
    </defs>
  </svg>
);

const KakaoIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M5.677 20.813C5.498 20.707 5.527 20.692 6.57 17.113C3.827 15.766 2 13.366 2 10.628C2 6.416 6.328 3 11.662 3H11.739C17.099 3 21.446 6.412 21.446 10.623C21.446 14.456 17.846 17.629 13.146 18.165C12.1006 18.2906 11.0434 18.2825 10 18.141C6.766 20.408 6.071 20.841 5.8 20.841C5.75719 20.8434 5.71457 20.8337 5.677 20.813Z"
      fill="#1A1A1A"
    />
  </svg>
);
